import * as React from "react";
import { cn } from "@/lib/utils";
import { motion, useReducedMotion } from "framer-motion";
import { CheckCircle2, XCircle, Calendar, FileText, Sparkles, Phone, Handshake } from "lucide-react";
import { TemperatureBadge } from "@/components/TemperatureBadge";

export type Temperature = "hot" | "warm" | "cold" | "stale" | null | undefined;
export type Outcome = "won" | "lost" | "not_fit" | null | undefined;
/** Pipeline stage for open leads (not won/lost). Supports DB values: new, contacted, qualified, appointment, proposal, negotiation. */
export type Stage =
  | "new"
  | "contacted"
  | "qualified"
  | "appointment"
  | "proposal"
  | "negotiation"
  | null
  | undefined;

export function formatMoneyCompact(value?: number | null, currency: "EUR" | "USD" = "EUR") {
  if (value == null || Number.isNaN(value)) return null;
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  const symbol = currency === "EUR" ? "€" : "$";
  if (abs >= 1_000_000) return `${sign}${symbol}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}${symbol}${(abs / 1_000).toFixed(2)}K`.replace(/\.00K$/, "K");
  return `${sign}${symbol}${abs.toFixed(0)}`;
}

export function TempPill({ value }: { value: Temperature }) {
  return <TemperatureBadge value={value} />;
}

export function NeutralStagePill({ stage }: { stage: Stage }) {
  const v = (stage ?? "").toLowerCase() as string;

  const common =
    "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium";
  const base = "bg-muted/25 text-muted-foreground border-border/60";

  const stageConfig: Record<
    string,
    { icon: React.ElementType; label: string }
  > = {
    new: { icon: Sparkles, label: "New" },
    contacted: { icon: Phone, label: "Contacted" },
    qualified: { icon: CheckCircle2, label: "Qualified" },
    appointment: { icon: Calendar, label: "Appointment" },
    proposal: { icon: FileText, label: "Proposal" },
    negotiation: { icon: Handshake, label: "Negotiation" },
  };

  const config = stageConfig[v] ?? { icon: Sparkles, label: v || "—" };
  const Icon = config.icon;

  return (
    <span className={cn(common, base)}>
      <Icon className="h-3.5 w-3.5 opacity-70" />
      {config.label}
    </span>
  );
}

export function OutcomeIcon({ outcome }: { outcome: Outcome }) {
  const v = (outcome ?? "").toLowerCase() as Outcome;
  if (v === "won") {
    return <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />;
  }
  if (v === "lost" || v === "not_fit") {
    return <XCircle className="h-4 w-4 text-rose-600 dark:text-rose-400" />;
  }
  return null;
}

export function DealChip({
  value,
  currency,
}: { value?: number | null; currency?: "EUR" | "USD" }) {
  const formatted = formatMoneyCompact(value, currency ?? "EUR");
  if (!formatted) return null;

  // Premium “revenue accent”: single strong signal
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold",
        "bg-primary/10 text-primary border-primary/20"
      )}
    >
      {formatted}
    </span>
  );
}

export function HotActivityBar({ active }: { active: boolean }) {
  const reduce = useReducedMotion();
  if (!active) return null;

  // Premium: subtle indeterminate sweep (no flashing background)
  if (reduce) {
    return <div className="mt-3 h-[3px] w-full rounded-full bg-red-500/15" />;
  }

  return (
    <div className="mt-3 h-[3px] w-full overflow-hidden rounded-full bg-muted/30">
      <motion.div
        className="h-full w-[35%] rounded-full bg-red-500/35"
        initial={{ x: "-40%" }}
        animate={{ x: "140%" }}
        transition={{ duration: 12, ease: "easeInOut", repeat: Infinity }}
        style={{ willChange: "transform" }}
      />
    </div>
  );
}
